import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Car, Sparkles, ShieldCheck, Droplets, Gauge, ArrowRight, Check, Zap } from "lucide-react";
import { useLang } from "@/i18n";
import PageSeo from "@/components/PageSeo";
import AutosFooter from "@/pages/AutosFooter";
import { fmt } from "@/data/autosCatalog";

const GOLD = "#C7A668";
const HERO = "https://images.unsplash.com/photo-1619692460304-67fd2b1e436d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBjYXIlMjBkYXJrJTIwZ2FyYWdlfGVufDB8fHx8MTc4NjQwMjkwOXww&ixlib=rb-4.1.0&q=85";
const INTERIOR = "https://images.unsplash.com/photo-1633348570573-2c70fe9129de?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwyfHxwb3JzY2hlJTIwaW50ZXJpb3IlMjBsZWF0aGVyfGVufDB8fHx8MTc4NjQwMjkwOXww&ixlib=rb-4.1.0&q=85";

function Reveal({ children, delay = 0, className, style }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.7, ease: "easeOut", delay }}
      className={className}
      style={style}
    >
      {children}
    </motion.div>
  );
}

export default function AutosLanding({ lang: langProp }) {
  const { L } = useLang();
  const en = langProp === "en";
  const quoteTo = en ? "/en/cars/quote" : "/autos/cotizar";

  const membershipsTo = en ? "/en/cars/memberships" : "/autos/membresias";

  const packages = [
    {
      key: "salida", icon: Car, price: 600,
      name: "La Salida",
      tag: en ? "Base service" : "Servicio base",
      d: en
        ? "Premium mobile wash. Hand exterior wash, full vacuum and interior wipe-down — for the car you drive often."
        : "Lavado premium a domicilio. Lavado exterior a mano, aspirado completo y limpieza interior — para el auto que usas seguido.",
      bullets: en
        ? ["Hand exterior wash, pH-neutral", "Full vacuum: seats, carpets, trunk", "Dashboard, console, doors & glass", "Tires and wheels with dressing"]
        : ["Lavado exterior a mano, pH neutro", "Aspirado: asientos, alfombras, cajuela", "Tablero, consola, puertas y vidrios", "Llantas y rines con abrillantador"],
    },
    {
      key: "cabina", icon: Sparkles, featured: true, price: 2400,
      name: "La Cabina",
      tag: en ? "The heart · interior" : "El corazón · interior",
      d: en
        ? "Base plus leather, carpet extraction, plastics, trunk and ozone. The interior looks brand new."
        : "Base más piel, extracción de alfombras, plásticos, cajuela y ozono. El interior queda como nuevo.",
      bullets: en
        ? ["Everything in La Salida", "Leather cleaning & conditioning", "Deep carpet extraction", "Plastics & trim treatment", "Odor neutralization (ozone)"]
        : ["Todo lo de La Salida", "Limpieza y acondicionado de piel", "Extracción profunda de alfombras", "Tratamiento de plásticos y molduras", "Neutralización de olores (ozono)"],
    },
    {
      key: "completo", icon: ShieldCheck, price: 5900,
      name: "El Detallado Completo",
      tag: en ? "Interior + exterior" : "Interior + exterior",
      d: en
        ? "La Cabina plus clay bar, 1-stage paint correction, premium wax and headlight polish. Showroom finish."
        : "La Cabina más clay bar, corrección de pintura 1 fase, encerado premium y pulido de faros. Luce showroom.",
      bullets: en
        ? ["Everything in La Cabina", "Clay bar decontamination", "Paint correction (1 stage)", "Premium wax / sealant", "Headlight restoration"]
        : ["Todo lo de La Cabina", "Descontaminación con clay bar", "Corrección de pintura (1 fase)", "Encerado premium / sellador", "Pulido de faros"],
    },
  ];

  const addons = en
    ? ["Ceramic coating", "PPF (paint protection film)", "Premium leather treatment", "Engine bay detail"]
    : ["Recubrimiento cerámico", "PPF (película protectora)", "Tratamiento de piel premium", "Detallado de motor"];

  const steps = [
    { icon: Droplets, t: en ? "Tell us the car" : "Cuéntanos el auto", d: en ? "Make, model, year and what matters to you — leather, ceramic, vinyl." : "Marca, modelo, año y lo que te importa — piel, cerámico, vinil." },
    { icon: Gauge, t: en ? "We quote & schedule" : "Cotizamos y agendamos", d: en ? "Transparent prices — the price builds in front of you, by car and service." : "Precios transparentes — el precio se arma frente a ti, por auto y servicio." },
    { icon: Check, t: en ? "We come to your garage" : "Vamos a tu garaje", d: en ? "Mobile service at your home or garage, with the same standard as our hangar work." : "Servicio móvil en tu casa o garaje, con el mismo estándar que en el hangar." },
  ];

  return (
    <div data-testid="autos-landing" className="bg-[#0A0A0B] text-[#F2EFE8]" style={{ overflowX: "clip" }}>
      <PageSeo
        esPath="/autos" enPath="/en/cars"
        lang={en ? "en" : "es"}
        title={en ? "Luxury Car Detailing in Mexico | Dukk" : "Detallado de Autos de Lujo en México | Dukk"}
        description={en
          ? "Dukk brings aviation-grade care to luxury cars in Mexico: full interior detailing, leather, ceramic, paint correction and mobile service to your garage."
          : "Dukk lleva el cuidado grado aeronáutico a autos de lujo en México: detallado interior completo, piel, cerámico, corrección de pintura y servicio móvil a tu garaje."}
      />

      {/* HERO */}
      <section className="relative min-h-screen flex flex-col justify-end pb-20 px-6 md:px-16">
        <div className="absolute inset-0">
          <img src={HERO} alt="" aria-hidden="true" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,10,11,.5) 0%, rgba(10,10,11,.4) 40%, #0A0A0B 100%)" }} />
        </div>
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }} className="relative z-10 max-w-3xl" style={{ paddingTop: 120 }}>
          <div className="font-mono" style={{ fontSize: 11, letterSpacing: ".24em", color: GOLD, marginBottom: 20 }}>
            {en ? "DUKK · AUTOMOBILES" : "DUKK · AUTOMÓVILES"}
          </div>
          <h1 className="font-display" style={{ fontSize: "clamp(38px,6vw,78px)", lineHeight: 1.03, letterSpacing: "-.025em", margin: 0, fontWeight: 300 }}>
            {en ? "Your car, ready" : "Tu auto, listo"}<br />
            <span style={{ color: GOLD }}>{en ? "before the event." : "antes del evento."}</span>
          </h1>
          <p style={{ color: "#C9CED4", fontSize: "clamp(15px,1.6vw,19px)", lineHeight: 1.6, marginTop: 24, maxWidth: 620, fontWeight: 300 }}>
            {en
              ? "Premium mobile wash and detailing. The same standard we bring to private jets."
              : "Lavado premium y detailing a domicilio. El mismo estándar que llevamos a los jets privados."}
          </p>
          <div className="flex flex-wrap gap-3" style={{ marginTop: 34 }}>
            <Link to={quoteTo} data-testid="autos-hero-cta" className="no-underline font-mono" style={{ background: "#F2EFE8", color: "#0a0a0a", fontWeight: 600, borderRadius: 999, padding: "14px 28px", fontSize: 13, letterSpacing: ".06em", display: "inline-flex", alignItems: "center", gap: 8 }}>
              {en ? "BUILD YOUR DUKK" : "ARMA TU DUKK"} <ArrowRight size={15} />
            </Link>
          </div>
        </motion.div>
      </section>

      {/* PACKAGES */}
      <section className="px-6 md:px-16 max-w-7xl mx-auto" style={{ paddingTop: 120, paddingBottom: 120 }}>
        <Reveal>
          <div className="font-mono" style={{ fontSize: 11, letterSpacing: ".2em", color: "#6c727a", marginBottom: 14 }}>{en ? "THREE STANDARDS" : "TRES ESTÁNDARES"}</div>
          <h2 className="font-display" style={{ fontSize: "clamp(28px,3.6vw,48px)", letterSpacing: "-.02em", margin: "0 0 8px", fontWeight: 300 }}>{en ? "Choose the level of care." : "Elige el nivel de cuidado."}</h2>
          <p style={{ color: "#9aa0a7", fontSize: 15, maxWidth: 560, fontWeight: 300 }}>{en ? "Transparent prices. Start with the essentials and add what your car needs — the price builds in front of you." : "Precios transparentes. Empieza con lo esencial y agrega lo que tu auto necesite — el precio se arma frente a ti."}</p>
        </Reveal>
        <div className="grid grid-cols-1 lg:grid-cols-3" style={{ gap: 24, marginTop: 48 }}>
          {packages.map((p, i) => {
            const Icon = p.icon;
            return (
              <Reveal key={p.key} delay={i * 0.08} className="h-full">
                <div data-testid={`autos-package-${p.key}`} className="h-full flex flex-col" style={{ padding: 32, background: p.featured ? "linear-gradient(160deg,#16130c,#0a0a0a 60%)" : "#121214", border: `1px solid ${p.featured ? GOLD : "rgba(199,166,104,.2)"}`, borderRadius: 4 }}>
                  {p.featured && <div className="font-mono" style={{ fontSize: 9.5, letterSpacing: ".16em", color: "#0a0a0a", background: GOLD, padding: "4px 11px", borderRadius: 999, alignSelf: "flex-start", marginBottom: 18 }}>{en ? "MOST CHOSEN" : "MÁS ELEGIDO"}</div>}
                  <Icon size={26} color={GOLD} strokeWidth={1.4} />
                  <div className="font-mono" style={{ fontSize: 10.5, letterSpacing: ".14em", color: GOLD, textTransform: "uppercase", marginTop: 20 }}>{p.tag}</div>
                  <div className="font-display" style={{ fontSize: "clamp(26px,3vw,36px)", lineHeight: 1.05, letterSpacing: "-.015em", color: p.featured ? GOLD : "#F2EFE8", marginTop: 8, fontWeight: 300 }}>{p.name}</div>
                  <div style={{ color: "#F2EFE8", fontSize: 20, fontWeight: 500, marginTop: 8 }}>{en ? "from " : "desde "}{fmt(p.price)}</div>
                  <p style={{ color: "#9aa0a7", fontSize: 14.5, lineHeight: 1.6, marginTop: 14, fontWeight: 300 }}>{p.d}</p>
                  <div className="space-y-2.5" style={{ flex: 1, marginTop: 24, marginBottom: 28 }}>
                    {p.bullets.map((b, j) => (
                      <div key={j} className="flex gap-2.5 items-start"><Check size={14} color={GOLD} strokeWidth={2} style={{ marginTop: 3, flex: "none" }} /><span style={{ color: "#C9CED4", fontSize: 14, lineHeight: 1.45, fontWeight: 300 }}>{b}</span></div>
                    ))}
                  </div>
                  <Link to={quoteTo} data-testid={`autos-package-cta-${p.key}`} className="no-underline font-mono" style={{ display: "inline-flex", alignItems: "center", gap: 8, alignSelf: "flex-start", color: p.featured ? "#0a0a0a" : GOLD, background: p.featured ? "#F2EFE8" : "transparent", border: p.featured ? "none" : `1px solid ${GOLD}`, borderRadius: 999, padding: "12px 24px", fontSize: 12, letterSpacing: ".06em" }}>
                    {en ? "BUILD THIS" : "ARMAR ESTE"} <ArrowRight size={14} />
                  </Link>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* ADD-ONS */}
      <section className="px-6 md:px-16 py-16 max-w-7xl mx-auto border-t border-white/8">
        <Reveal>
          <div className="font-mono" style={{ fontSize: 11, letterSpacing: ".2em", color: "#6c727a", marginBottom: 18 }}>{en ? "ADD-ONS" : "COMPLEMENTOS"}</div>
          <div className="flex flex-wrap gap-3">
            {addons.map((a) => (
              <span key={a} className="font-mono" style={{ fontSize: 12.5, color: "#C9CED4", border: "1px solid rgba(199,166,104,.28)", borderRadius: 999, padding: "9px 18px" }}>{a}</span>
            ))}
          </div>
        </Reveal>
      </section>

      {/* PROCESS + IMAGE */}
      <section className="grid lg:grid-cols-2">
        <div className="relative min-h-[360px]">
          <img src={INTERIOR} alt={en ? "Luxury car interior detailing" : "Detallado de interior de auto de lujo"} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(10,10,11,.2), rgba(10,10,11,.85))" }} />
        </div>
        <div className="px-6 md:px-16 py-24 flex flex-col justify-center">
          <Reveal>
            <div className="font-mono" style={{ fontSize: 11, letterSpacing: ".2em", color: "#6c727a", marginBottom: 14 }}>{en ? "HOW IT WORKS" : "CÓMO FUNCIONA"}</div>
            <h2 className="font-display" style={{ fontSize: "clamp(26px,3.4vw,44px)", letterSpacing: "-.02em", margin: "0 0 36px", fontWeight: 300 }}>{en ? "Three steps. Zero friction." : "Tres pasos. Cero fricción."}</h2>
          </Reveal>
          <div className="space-y-8">
            {steps.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal key={i} delay={i * 0.06} className="flex gap-5 items-start">
                  <Icon size={22} color={GOLD} strokeWidth={1.4} style={{ flex: "none", marginTop: 3 }} />
                  <div>
                    <div style={{ color: "#F2EFE8", fontSize: 17, fontWeight: 500 }}>{s.t}</div>
                    <div style={{ color: "#9aa0a7", fontSize: 14.5, lineHeight: 1.55, marginTop: 6, fontWeight: 300 }}>{s.d}</div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* EXPRESS — DUKK YA */}
      <section className="px-6 md:px-16 py-16 max-w-7xl mx-auto">
        <Reveal>
          <div className="relative overflow-hidden rounded-sm p-8 md:p-12 flex flex-col md:flex-row md:items-center gap-6 justify-between" style={{ border: `1px solid ${GOLD}`, background: "linear-gradient(120deg,#16130c,#0a0a0a 70%)" }}>
            <div>
              <div className="flex items-center gap-2 font-mono" style={{ fontSize: 11, letterSpacing: ".18em", color: GOLD }}><Zap size={16} /> DUKK YA</div>
              <h2 className="font-display" style={{ fontSize: "clamp(24px,3vw,38px)", letterSpacing: "-.02em", margin: "12px 0 8px", fontWeight: 300 }}>{en ? "Need it today?" : "¿Lo necesitas hoy?"}</h2>
              <p style={{ color: "#C9CED4", fontSize: 15, maxWidth: 520, fontWeight: 300 }}>{en ? "Dukk Ya arrives in under 24 hours, subject to availability. Same standard, on your clock." : "Dukk Ya llega en menos de 24 horas, sujeto a disponibilidad. El mismo estándar, a tu ritmo."}</p>
            </div>
            <Link to={quoteTo} data-testid="autos-express-cta" className="no-underline font-mono" style={{ background: GOLD, color: "#0a0a0a", fontWeight: 600, borderRadius: 999, padding: "14px 26px", fontSize: 13, letterSpacing: ".05em", display: "inline-flex", alignItems: "center", gap: 8, whiteSpace: "nowrap" }}>
              {en ? "I WANT MY DUKK YA" : "QUIERO MI DUKK YA"} <ArrowRight size={15} />
            </Link>
          </div>
        </Reveal>
      </section>

      {/* MEMBERSHIPS TEASER */}
      <section id="membresias" className="px-6 md:px-16 max-w-7xl mx-auto border-t border-white/8" style={{ paddingTop: 120, paddingBottom: 120 }}>
        <Reveal>
          <div className="rounded-sm flex flex-col md:flex-row md:items-center justify-between" style={{ padding: 40, gap: 32, border: `1px solid rgba(199,166,104,.28)`, background: "linear-gradient(120deg,#16130c,#0a0a0a 70%)" }}>
            <div>
              <div className="font-mono" style={{ fontSize: 11, letterSpacing: ".2em", color: GOLD, marginBottom: 14 }}>{en ? "MEMBERSHIPS" : "MEMBRESÍAS"}</div>
              <h2 className="font-display" style={{ fontSize: "clamp(26px,3.4vw,44px)", letterSpacing: "-.02em", margin: "0 0 12px", fontWeight: 300, maxWidth: 620 }}>{en ? "The one-time is the door. The membership is the relationship." : "El one-time es la puerta. La membresía es la relación."}</h2>
              <p style={{ color: "#9aa0a7", fontSize: 15, maxWidth: 560, fontWeight: 300 }}>{en ? "Scheduled premium washes, module discounts and multi-car Garage plans. The more cars Dukk cares for at your home, the more you belong to the house." : "Lavados premium agendados, descuentos en módulos y planes Garaje multi-auto. Mientras más autos cuida Dukk de tu casa, más eres de la casa."}</p>
            </div>
            <Link to={membershipsTo} data-testid="autos-memberships-cta" className="no-underline font-mono" style={{ background: GOLD, color: "#0a0a0a", fontWeight: 600, borderRadius: 999, padding: "14px 26px", fontSize: 13, letterSpacing: ".05em", display: "inline-flex", alignItems: "center", gap: 8, whiteSpace: "nowrap" }}>
              {en ? "VIEW MEMBERSHIPS" : "VER MEMBRESÍAS"} <ArrowRight size={15} />
            </Link>
          </div>
        </Reveal>
      </section>

      {/* CTA */}
      <section className="px-6 md:px-16 py-24 text-center border-t border-[#C7A668]/18" style={{ background: "radial-gradient(85% 120% at 50% 0%, rgba(199,166,104,.1), transparent 55%)" }}>
        <Reveal>
          <h2 className="font-display" style={{ fontSize: "clamp(30px,4.6vw,60px)", lineHeight: 1.06, letterSpacing: "-.025em", margin: "0 auto", maxWidth: 760, fontWeight: 300 }}>
            {en ? "The same standard, for what you drive." : "El mismo estándar, para lo que manejas."}
          </h2>
          <div className="mt-10 flex justify-center">
            <Link to={quoteTo} data-testid="autos-final-cta" className="no-underline font-mono" style={{ background: "#F2EFE8", color: "#0a0a0a", fontWeight: 600, borderRadius: 999, padding: "15px 32px", fontSize: 14, letterSpacing: ".06em", display: "inline-flex", alignItems: "center", gap: 9 }}>
              {en ? "BUILD YOUR DUKK" : "ARMA TU DUKK"} <ArrowRight size={16} />
            </Link>
          </div>
        </Reveal>
      </section>

      <AutosFooter lang={langProp} />
    </div>
  );
}
